/**
 * GrowthChannels — Revenue by channel (premium design with donut chart)
 */
import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const channelLabels = {
  online: "Online",
  table: "Mesa",
  whatsapp: "WhatsApp",
  delivery: "Delivery",
  pos: "POS",
  phone: "Telefone",
  aggregator: "Agregador",
  unknown: "Outro"
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0];
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 shadow-xl text-white">
      <p className="text-sm font-semibold">{data.name}</p>
      <p className="text-sm" style={{ color: data.fill }}>€{data.value.toFixed(2)}</p>
    </div>
  );
};

export default function GrowthChannels({ authAxios, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoading(true);
        const res = await authAxios.get(`/growth/channels?days=${days}`);
        setData(res.data);
      } catch (err) {
        console.error("Error fetching channels:", err);
        toast.error("Erro ao carregar canais");
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, [days, authAxios]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-40" />
          </CardContent>
        </Card>
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-80 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Array(4).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data || !data.channels?.length) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Sem dados de canais</p>
        </CardContent>
      </Card>
    );
  }

  const { channels, total_revenue } = data;
  const topChannel = channels.length > 0 ? channels[0] : null;

  const chartData = channels.map((ch) => ({
    name: channelLabels[ch.channel] || ch.channel,
    value: parseFloat(ch.revenue.toFixed(2)),
    raw_channel: ch.channel
  }));

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-1">
                Receita Total por Canal
              </p>
              <p className="text-3xl font-bold text-slate-900">€{total_revenue.toFixed(2)}</p>
              {topChannel && (
                <p className="text-sm text-slate-600 mt-3">
                  Canal principal: <strong>{channelLabels[topChannel.channel]}</strong> ({topChannel.percent_of_total.toFixed(1)}%)
                </p>
              )}
            </div>
            <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-blue-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Donut Chart */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Distribuição por Canal</CardTitle>
            <p className="text-xs text-slate-600 mt-1">Percentual de receita</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={130}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={2}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Detailed Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Detalhe por Canal</CardTitle>
            <p className="text-xs text-slate-600 mt-1">Receita e volume de pedidos</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {channels.map((ch, idx) => (
                <div key={ch.channel} className="pb-4 border-b border-slate-200 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-slate-900">{channelLabels[ch.channel] || ch.channel}</p>
                        <p className="text-xs text-slate-500">{ch.orders} pedidos</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">€{ch.revenue.toFixed(2)}</p>
                      <p className="text-xs font-semibold text-slate-600">{ch.percent_of_total.toFixed(1)}%</p>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${ch.percent_of_total}%`,
                        backgroundColor: COLORS[idx % COLORS.length]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
